// ── Schedule Health Panel ───────────────────────────────────────────────────
// Continuous-validation surface for the saved schedule. Reuses the same
// constraint engine the wizard runs. Issues are grouped by kind so the
// provider can scan totals at a glance and drill into affected blocks /
// students. Iter 28 v2 turns it into a triage workbench: each violation
// row exposes contextual actions (Open in Builder / Open Service
// Commitments / Open Constraints / Delete block) that deep-link to the
// right correction surface.
import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
// FP3-035F Issue 2 — Preserve ?as_provider= when navigating from
// the schedule health panel.
import useShadowNavigate from "@/lib/useShadowNavigate";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  ArrowsClockwise,
  CheckCircle,
  Warning,
  CalendarBlank,
  Clock,
  ShieldWarning,
  GraduationCap,
  Bed,
  ListChecks,
  Wrench,
  IdentificationCard,
  Trash,
  ArrowSquareOut,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import { DAYS, fmtTime } from "./utils";
import ConfirmDialog from "@/components/common/ConfirmDialog";

// Display metadata per violation kind. `severity` drives palette + sort;
// `label` is shown on the group header; `Icon` sits beside each card.
const KIND_META = {
  cross_provider:                { severity: "high",   label: "Cross-provider conflict",   Icon: Warning,        order: 1 },
  provider_blocked_time:         { severity: "high",   label: "Provider blocked-time overlap", Icon: ShieldWarning, order: 2 },
  attendance_window:             { severity: "high",   label: "Outside attendance window", Icon: Clock,          order: 3 },
  flex_day_closed:               { severity: "high",   label: "School closed (flex day)",  Icon: CalendarBlank,  order: 4 },
  flex_day_outside_window:       { severity: "medium", label: "Outside flex-day window",   Icon: CalendarBlank,  order: 5 },
  grade_constraint:              { severity: "medium", label: "Overlaps grade rule",       Icon: GraduationCap,  order: 6 },
  provider_unavailable_day:      { severity: "medium", label: "Provider unavailable day",  Icon: Bed,            order: 7 },
  provider_partial_window:       { severity: "medium", label: "Outside provider window",   Icon: Clock,          order: 8 },
  archived_student_block:        { severity: "medium", label: "Archived/graduated student",Icon: GraduationCap,  order: 9 },
  service_obligation_no_blocks:  { severity: "high",   label: "No blocks for active commitment", Icon: ListChecks, order: 10 },
  assistant_no_blocks_for_commitment: { severity: "medium", label: "Assistant has no scheduled blocks", Icon: ListChecks, order: 10 },
  service_obligation_under_minutes:{ severity: "medium", label: "Under-scheduled minutes", Icon: ListChecks,     order: 11 },
  service_obligation_over_minutes: { severity: "low",  label: "Over-scheduled minutes",    Icon: ListChecks,     order: 12 },
};

const SEVERITY_STYLES = {
  high:   { bg: "bg-[#FBEFEC]", text: "text-[#B85C5C]", border: "border-[#B85C5C]" },
  medium: { bg: "bg-[#FDF8F3]", text: "text-[#D9A05B]", border: "border-[#D9A05B]" },
  low:    { bg: "bg-[#EFF3F1]", text: "text-[#6B8579]", border: "border-[#6B8579]" },
};

function metaFor(kind) {
  return (
    KIND_META[kind] || {
      severity: "low",
      label: kind,
      Icon: Warning,
      order: 99,
    }
  );
}

export default function HealthPanel({ provider, schedulePeriodId, onCountChange }) {
  const navigate = useShadowNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  // FP3-032D — Pending delete state powers the in-app confirmation
  // modal that replaces the old window.confirm popup.
  const [pendingDeleteBlock, setPendingDeleteBlock] = useState(null);

  const load = useCallback(
    async ({ silent = false } = {}) => {
      if (!provider?.id) return;
      if (silent) setRefreshing(true);
      else setLoading(true);
      try {
        const res = await api.getScheduleHealthCheck({
          provider_id: provider.id,
          ...(schedulePeriodId ? { schedule_period_id: schedulePeriodId } : {}),
        });
        setData(res.data);
        setError(null);
      } catch (e) {
        setError(e?.response?.data?.detail || e.message || "Failed to load health check");
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [provider?.id, schedulePeriodId]
  );

  useEffect(() => {
    load();
  }, [load]);

  // Notify parent so the tab can show a count badge.
  useEffect(() => {
    if (data && onCountChange) onCountChange(data.summary?.total_violations || 0);
  }, [data, onCountChange]);

  // ── Resolve actions ────────────────────────────────────────────────────────
  // openBuilder — switch Schedule to Builder tab; optionally tag student/block.
  const openBuilder = useCallback(
    (v) => {
      const params = new URLSearchParams({ tab: "builder" });
      if (v?.block_id) params.set("highlight", v.block_id);
      if (v?.student_id) params.set("studentId", v.student_id);
      navigate(`/schedule?${params.toString()}`);
    },
    [navigate]
  );

  // openConstraints — school-level rules: grade schedules, flex days, school
  // attendance windows. Routes to the new School tab.
  const openSchoolConstraints = useCallback(() => {
    navigate(`/schedule?tab=school-rules`);
  }, [navigate]);

  // openProviderConstraints — provider availability + blocked-times + lunch.
  // Routes to the new Provider tab so the user lands directly on their own
  // availability editor.
  const openProviderConstraints = useCallback(() => {
    navigate(`/schedule?tab=provider-rules`);
  }, [navigate]);

  // openServiceCommitments — student page → Service Demographics tab.
  const openServiceCommitments = useCallback(
    (v) => {
      if (!v?.student_id) return;
      navigate(`/students/${v.student_id}?tab=service-demographics`);
    },
    [navigate]
  );

  // deleteBlock — destructive resolve for archived_student_block. Refreshes
  // the panel inline so the row disappears without a page reload.
  // FP3-032D — Confirmation now uses the calm in-app ConfirmDialog
  // (state lifted to the panel so violation rows trigger it).
  const requestDeleteBlock = useCallback((v) => {
    if (!v?.block_id) return;
    setPendingDeleteBlock(v);
  }, []);

  const confirmDeleteBlock = useCallback(async () => {
    if (!pendingDeleteBlock?.block_id) return;
    setDeletingId(pendingDeleteBlock.block_id);
    try {
      // FP3-124 §8 — Delete is effective-date forward; historical
      // dates keep the block visible and their logged sessions
      // survive. HealthPanel invalidation is the FE trigger.
      const _n = new Date();
      const _eff = `${_n.getFullYear()}-${String(_n.getMonth() + 1).padStart(2, "0")}-${String(_n.getDate()).padStart(2, "0")}`;
      await api.deleteRecurringBlock(pendingDeleteBlock.block_id, _eff);
      toast.success("Block deleted");
      setPendingDeleteBlock(null);
      await load({ silent: true });
    } catch (e) {
      toast.error(`Delete failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setDeletingId(null);
    }
  }, [pendingDeleteBlock, load]);

  if (loading) {
    return (
      <div className="space-y-3 max-w-4xl" data-testid="health-panel-loading">
        <Skeleton className="h-16 w-full rounded-xl" />
        <Skeleton className="h-32 w-full rounded-xl" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl" data-testid="health-panel-error">
        <Card className="border-l-4 border-[#B85C5C] rounded-xl">
          <CardContent className="p-4 text-sm text-[#B85C5C]">{error}</CardContent>
        </Card>
      </div>
    );
  }

  const total = data?.summary?.total_violations || 0;
  const checked = data?.checked_blocks || 0;
  const violations = data?.violations || [];

  // Group violations by kind, then sort groups by KIND_META.order.
  const grouped = violations.reduce((acc, v) => {
    (acc[v.kind] = acc[v.kind] || []).push(v);
    return acc;
  }, {});
  const groupKeys = Object.keys(grouped).sort(
    (a, b) => metaFor(a).order - metaFor(b).order
  );

  return (
    <div className="space-y-4 max-w-4xl" data-testid="health-panel">
      {/* Summary header */}
      <Card className="rounded-xl border border-[#E5E3DB] shadow-sm">
        <CardContent className="p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {total === 0 ? (
              <CheckCircle size={24} weight="duotone" className="text-[#3F7A5C]" />
            ) : (
              <Warning size={24} weight="duotone" className="text-[#D9A05B]" />
            )}
            <div>
              <div className="text-base font-semibold text-[#1F2937] heading-font" data-testid="health-total-count">
                {total === 0
                  ? "All clear — schedule is healthy"
                  : `${total} issue${total === 1 ? "" : "s"} found`}
              </div>
              <div className="text-xs text-[#6B7280]">
                Checked {checked} block{checked === 1 ? "" : "s"} against provider availability,
                grade rules, flex days, attendance, and service commitments
              </div>
            </div>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={() => load({ silent: true })}
            disabled={refreshing}
            data-testid="health-recheck"
            className="rounded-lg h-8 text-xs border-[#d4d2cc]"
          >
            <ArrowsClockwise
              size={12}
              weight="bold"
              className={`mr-1 ${refreshing ? "animate-spin" : ""}`}
            />
            {refreshing ? "Rechecking…" : "Recheck"}
          </Button>
        </CardContent>
      </Card>

      {/* Kind breakdown chips */}
      {total > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="health-kind-chips">
          {groupKeys.map((k) => {
            const m = metaFor(k);
            const st = SEVERITY_STYLES[m.severity];
            return (
              <Badge
                key={k}
                data-testid={`health-chip-${k}`}
                className={`${st.bg} ${st.text} hover:bg-opacity-90 text-[11px] px-2 py-1`}
              >
                {grouped[k].length} · {m.label}
              </Badge>
            );
          })}
        </div>
      )}

      {/* Empty state */}
      {total === 0 && (
        <div className="text-center py-10" data-testid="health-empty">
          <CheckCircle size={36} weight="duotone" className="text-[#8FA998] mx-auto mb-2" />
          <p className="text-sm text-[#6B7280]">
            No violations against the current constraint set. Your saved schedule is healthy.
          </p>
        </div>
      )}

      {/* Grouped violations */}
      {groupKeys.map((kind) => {
        const m = metaFor(kind);
        const st = SEVERITY_STYLES[m.severity];
        const items = grouped[kind];
        const Icon = m.Icon;
        return (
          <Card
            key={kind}
            data-testid={`health-group-${kind}`}
            className={`rounded-xl border-l-4 ${st.border} border border-[#E5E3DB] shadow-sm`}
          >
            <CardContent className="p-0">
              <div className={`flex items-center gap-2 px-4 py-2 ${st.bg} rounded-t-xl`}>
                <Icon size={16} weight="duotone" className={st.text} />
                <span className={`text-sm font-semibold ${st.text}`}>{m.label}</span>
                <span className="ml-auto text-[11px] text-[#4B5563]">
                  {items.length} issue{items.length === 1 ? "" : "s"}
                </span>
              </div>
              <div className="divide-y divide-[#ECEAE4]">
                {items.map((v, idx) => (
                  <ViolationRow
                    key={`${v.block_id || "no-block"}-${idx}`}
                    v={v}
                    testidPrefix={`health-row-${kind}-${idx}`}
                    onOpenBuilder={openBuilder}
                    onOpenSchoolConstraints={openSchoolConstraints}
                    onOpenProviderConstraints={openProviderConstraints}
                    onOpenServiceCommitments={openServiceCommitments}
                    onDeleteBlock={requestDeleteBlock}
                    isDeleting={deletingId === v.block_id}
                  />
                ))}
              </div>
            </CardContent>
          </Card>
        );
      })}
      {/* FP3-032D — Calm in-app confirmation for recurring-block delete. */}
      <ConfirmDialog
        open={!!pendingDeleteBlock}
        onOpenChange={(o) => !deletingId && setPendingDeleteBlock(o ? pendingDeleteBlock : null)}
        title="Delete this recurring block?"
        description={(() => {
          if (!pendingDeleteBlock) return "";
          const v = pendingDeleteBlock;
          const when = v.start_time && v.end_time
            ? `${DAYS[v.day_of_week]} ${fmtTime(v.start_time)}–${fmtTime(v.end_time)} · `
            : "";
          return `${when}${v.student_name || "This block"} will be permanently removed from your schedule.`;
        })()}
        confirmLabel="Delete block"
        destructive
        onConfirm={confirmDeleteBlock}
        testId="health-delete-block-confirm"
      />
    </div>
  );
}

// ── Action availability per violation kind ────────────────────────────────
// Drives which buttons appear on a row. Each entry is a list of action ids;
// the row component renders only the actions present here for the kind.
const KIND_ACTIONS = {
  cross_provider:                  ["builder"],
  provider_blocked_time:           ["builder", "availability"],
  attendance_window:               ["builder", "service_commitments"],
  flex_day_closed:                 ["builder", "constraints"],
  flex_day_outside_window:         ["builder", "constraints"],
  grade_constraint:                ["builder", "constraints"],
  provider_unavailable_day:        ["availability", "delete"],
  provider_partial_window:         ["availability"],
  archived_student_block:          ["delete", "builder"],
  service_obligation_no_blocks:    ["service_commitments", "builder"],
  service_obligation_under_minutes:["service_commitments", "builder"],
  service_obligation_over_minutes: ["service_commitments", "builder"],
};

function ViolationRow({
  v,
  testidPrefix,
  onOpenBuilder,
  onOpenSchoolConstraints,
  onOpenProviderConstraints,
  onOpenServiceCommitments,
  onDeleteBlock,
  isDeleting,
}) {
  const day = typeof v.day_of_week === "number" ? DAYS[v.day_of_week] : null;
  const time =
    v.start_time && v.end_time ? `${fmtTime(v.start_time)}–${fmtTime(v.end_time)}` : null;
  const actions = KIND_ACTIONS[v.kind] || [];

  return (
    <div
      data-testid={testidPrefix}
      className="px-4 py-3 flex items-start justify-between gap-4 hover:bg-[#FAFAF7]"
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className="text-sm font-medium text-[#1F2937] truncate">
            {v.student_name || "—"}
          </span>
          {v.commitment_label && (
            <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#EFF3F1] text-[#3F7A5C]">
              {v.commitment_label}
            </span>
          )}
        </div>
        <div className="text-xs text-[#6B7280] mt-0.5">{v.details}</div>

        {/* Resolve action buttons — rendered in KIND_ACTIONS array order so
            the leftmost button is the primary action for the kind. */}
        {actions.length > 0 && (
          <div
            className="flex flex-wrap items-center gap-1.5 mt-2"
            data-testid={`${testidPrefix}-actions`}
          >
            {actions.map((a) => {
              if (a === "builder") {
                return (
                  <Button
                    key="builder"
                    size="sm"
                    variant="outline"
                    onClick={() => onOpenBuilder(v)}
                    data-testid={`${testidPrefix}-action-builder`}
                    className="rounded-md h-7 text-[11px] border-[#d4d2cc] text-[#1F2937]"
                  >
                    <Wrench size={11} weight="duotone" className="mr-1 text-[#1E3F33]" />
                    Open in Builder
                  </Button>
                );
              }
              if (a === "service_commitments") {
                return (
                  <Button
                    key="service_commitments"
                    size="sm"
                    variant="outline"
                    onClick={() => onOpenServiceCommitments(v)}
                    data-testid={`${testidPrefix}-action-service-commitments`}
                    className="rounded-md h-7 text-[11px] border-[#d4d2cc] text-[#1F2937]"
                  >
                    <IdentificationCard size={11} weight="duotone" className="mr-1 text-[#3F7A5C]" />
                    Service Commitments
                  </Button>
                );
              }
              if (a === "constraints") {
                return (
                  <Button
                    key="constraints"
                    size="sm"
                    variant="outline"
                    onClick={onOpenSchoolConstraints}
                    data-testid={`${testidPrefix}-action-constraints`}
                    className="rounded-md h-7 text-[11px] border-[#d4d2cc] text-[#1F2937]"
                  >
                    <ArrowSquareOut size={11} weight="bold" className="mr-1 text-[#5C7088]" />
                    Open School Rules
                  </Button>
                );
              }
              if (a === "availability") {
                return (
                  <Button
                    key="availability"
                    size="sm"
                    variant="outline"
                    onClick={onOpenProviderConstraints}
                    data-testid={`${testidPrefix}-action-availability`}
                    className="rounded-md h-7 text-[11px] border-[#d4d2cc] text-[#1F2937]"
                  >
                    <Bed size={11} weight="duotone" className="mr-1 text-[#5C7088]" />
                    Provider Availability
                  </Button>
                );
              }
              if (a === "delete" && v.block_id) {
                return (
                  <Button
                    key="delete"
                    size="sm"
                    variant="outline"
                    onClick={() => onDeleteBlock(v)}
                    disabled={isDeleting}
                    data-testid={`${testidPrefix}-action-delete`}
                    className="rounded-md h-7 text-[11px] border-[#E5C7C0] text-[#B85C5C] hover:bg-[#FBEFEC]"
                  >
                    <Trash size={11} weight="bold" className="mr-1" />
                    {isDeleting ? "Deleting…" : "Delete block"}
                  </Button>
                );
              }
              return null;
            })}
          </div>
        )}
      </div>
      <div className="text-right text-[11px] text-[#4B5563] whitespace-nowrap shrink-0">
        {day && <div>{day}</div>}
        {time && <div className="tabular-nums">{time}</div>}
      </div>
    </div>
  );
}
